import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { updateParentInfoVisibilityThunk } from '../../../../accounts/slices/accounts/thunks/update-parent-info-visibility.thunk.ts';
import { fetchAndAssignCurrentUserThunk } from '../../../../accounts/slices/auth/thunks/fetch-and-assign-current-user.thunk.ts';
import { processMagicAuthTokenThunk } from '../../../../accounts/slices/auth/thunks/process-magic-auth-token.thunk.ts';
import { selectCurrentEnvironmentName } from '../../../../environment/selectors/current-environment-name.selector.ts';
import { checkOrSelectEnvironmentThunk } from '../../../../environment/slices/environment/thunks/check-or-select-environment.thunk.ts';
import { updateAppStateThunk } from '../../../../learn/slices/app-state/thunks/update-app-state.thunk.ts';
import { refreshLastCompletedStepThunk } from '../../../../learn/slices/progress/thunks/refresh-last-completed-step.thunk.ts';

import { ThunkApiConfigType } from '../../../store/main.store.ts';
import { AppStateEnum } from '../../../types/app-state.types.ts';
import { getAssetPreloadingUtil } from '../../../utils/asset/asset-preloading.util.ts';
import { getAssetUtil } from '../../../utils/asset/assets.util.ts';
import { getAudioContextUtil } from '../../../utils/audio/audio-context.util.ts';
import { getAudioLettersProviderUtil } from '../../../utils/audio/audio-phoneme-provider.util.ts';
import { getAudioSfxProviderUtil } from '../../../utils/audio/audio-sfx-provider.util.ts';
import { getSentryUtil } from '../../../utils/monitoring/sentry.util.ts';
import { getTrackerUtil } from '../../../utils/trackers/tracker.util.ts';

import { APP_LIFECYCLE_SLICE_NAME, AppLifecycleSliceState } from '../app-lifecycle.slice.types.ts';

export const loadAppThunk = createAsyncThunk<void, undefined, ThunkApiConfigType>(
  `${APP_LIFECYCLE_SLICE_NAME}/loadApp`,
  async (_, thunkAPI) => {
    const timeKey = 'Loading app';
    console.time(timeKey);
    // Initialize singleton utils
    getTrackerUtil();
    getSentryUtil();
    getAudioSfxProviderUtil();
    getAudioLettersProviderUtil();
    const audioContextInitPromise = getAudioContextUtil().init();
    thunkAPI.dispatch(updateAppStateThunk({ state: AppStateEnum.Booting }));

    await thunkAPI.dispatch(checkOrSelectEnvironmentThunk()).unwrap();

    const env = selectCurrentEnvironmentName(thunkAPI.getState());

    console.timeLog(timeKey, 'Loaded Utils and environment');
    await Promise.allSettled([
      thunkAPI.dispatch(fetchAndAssignCurrentUserThunk()).unwrap(),
      getAssetUtil().loadAllData(env),
    ]);
    console.timeLog(timeKey, 'Loaded Current User and Data');

    await thunkAPI.dispatch(processMagicAuthTokenThunk()).unwrap();

    const preloadingUtil = getAssetPreloadingUtil();
    await Promise.allSettled([
      preloadingUtil.preloadCriticalAssets(),
      thunkAPI.dispatch(updateParentInfoVisibilityThunk()).unwrap(),
      thunkAPI.dispatch(refreshLastCompletedStepThunk()).unwrap(),
    ]);
    console.timeLog(
      timeKey,
      'Preloaded Critical Assets, Updated Parent Info Visibility and Refreshed Last Completed Step'
    );

    await audioContextInitPromise;

    preloadingUtil.preloadAssetsInBackground();
    thunkAPI.dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyToStart }));
    console.timeEnd(timeKey);
  }
);

export const loadAppThunkExtraReducers = (builder: ActionReducerMapBuilder<AppLifecycleSliceState>): void => {
  builder.addCase(loadAppThunk.fulfilled, (state) => {
    state.isAppInitialized = true;
  });

  builder.addCase(loadAppThunk.rejected, (state) => {
    state.isAppInitialized = true;
  });
};
